/**
 * SocialAccountsView — FEAT-095 / S-399.
 *
 * The first frontend surface for the account/credential model built in
 * S-395 through S-398 (all backend-only until now). Lists a workspace's
 * own connected social accounts and accounts shared to it, and lets a
 * member connect a new account through a real Zernio OAuth round trip.
 *
 * Pattern: AmplifiersView.tsx's workspace-selector shell (loading/error/
 * empty states, sessionStorage-persisted selection) and its
 * useEnsureActiveWorkspace/useActiveWorkspace wiring (AmplifiersView.tsx:
 * 368-373) — load-bearing here, not cosmetic: POST .../connect and POST
 * /social-accounts/:id/grants both check the JWT's agencyclient_id claim
 * against the target workspace (not just the displayed selection), so a
 * mismatched claim 403s with not_a_member/unauthorized regardless of what
 * the dropdown shows.
 *
 * Deliberately publication-agnostic (unlike AmplifiersView) — accounts are
 * workspace-scoped only.
 */

import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { SectionHeader } from "@/components/SectionHeader";
import { ModalShell } from "@/components/SettingsModalShell";
import { ApiError, useApi, useApiMutation, useApiQuery } from "@/lib/api";
import { useActiveWorkspace, useEnsureActiveWorkspace } from "@/lib/useActiveWorkspace";
import {
  channelState,
  describeChannelsError,
  describeApiErrorMessage,
  type BufferChannelRow,
  type ChannelState,
} from "@/lib/buffer-channels";
import { useSettingsWorkspaces } from "@/lib/useSettingsWorkspaces";

const PLATFORMS = ["linkedin", "x", "instagram", "threads"] as const;
type Platform = (typeof PLATFORMS)[number];

const PLATFORM_LABEL: Record<Platform, string> = {
  linkedin: "LinkedIn",
  x: "X",
  instagram: "Instagram",
  threads: "Threads",
};

// Founder request (2026-09-18) — a platform icon is more eye-catching and
// scannable than the account name's old "(Platform)" suffix, especially
// once one platform can have several distinct accounts in the same list.
// Simple, deliberately non-brand-exact shapes (a colored badge + a
// recognizable letter/mark), not hand-reproduced official logo artwork —
// this codebase has no icon-library dependency, and four fixed platforms
// don't warrant adding one just for pixel-perfect brand marks.
function PlatformIcon({ platform }: { platform: Platform }) {
  const size = 22;
  const common = { width: size, height: size, viewBox: "0 0 24 24", role: "img" as const, "aria-label": PLATFORM_LABEL[platform] };
  const badge = (fill: string) => <rect width="24" height="24" rx="5" fill={fill} />;
  const letter = (char: string, fontSize = 13) => (
    <text x="12" y="16.5" textAnchor="middle" fontSize={fontSize} fontWeight="700" fontFamily="Arial, sans-serif" fill="#fff">
      {char}
    </text>
  );
  switch (platform) {
    case "linkedin":
      return (
        <svg {...common}>
          {badge("#0A66C2")}
          {letter("in")}
        </svg>
      );
    case "x":
      return (
        <svg {...common}>
          {badge("#000000")}
          {letter("X", 14)}
        </svg>
      );
    case "instagram":
      return (
        <svg {...common}>
          {badge("#E4405F")}
          <rect x="6" y="6" width="12" height="12" rx="4" fill="none" stroke="#fff" strokeWidth="1.6" />
          <circle cx="12" cy="12" r="3.1" fill="none" stroke="#fff" strokeWidth="1.6" />
          <circle cx="16.3" cy="7.7" r="1" fill="#fff" />
        </svg>
      );
    case "threads":
      return (
        <svg {...common}>
          {badge("#000000")}
          {letter("@", 14)}
        </svg>
      );
    default:
      return null;
  }
}

// Distinct, diagnostic messages per social-accounts.ts's callback error
// codes (spec:149-152) — a founder hitting profile_mismatch needs
// different guidance than no_new_account_found.
const CONNECT_ERROR_MESSAGES: Record<string, string> = {
  no_new_account_found:
    "No new account was found. Make sure you completed the connection for a NEW account — reconnecting an already-connected one won't register.",
  profile_mismatch:
    "That account is already connected to a different workspace and can't be connected here.",
  ambiguous_new_account:
    "More than one new account appeared during that connection attempt. Please try connecting again, one account at a time.",
};

const SA_WS_KEY = "ccm:social-accounts:active_ws";

interface Agencyclient {
  id: number;
  name: string;
  slug: string;
}

interface SharedWithEntry {
  agencyclient_id: number;
  agencyclient_name: string;
}

interface SocialAccount {
  id: number;
  provider: string;
  platform: string;
  external_account_id: string;
  display_name: string;
  status: string;
  /** FEAT-107 / S-435 — the provider's words while status is needs_attention. */
  attention_reason?: string | null;
  owning_agencyclient_id: number;
  is_shared_to_me: boolean;
  shared_with?: SharedWithEntry[];
  // CCM-87 — this account's own persistent voice override; null = uses the
  // publication's own voice when amplified through this account.
  voice_profile_id: number | null;
}

/**
 * FEAT-107 / S-435 — an account CCM stopped sending to says so, with the
 * provider's reason on hover; the Social Calendar has the actions.
 */
function AccountStatusPill({ account }: { account: Pick<SocialAccount, "status" | "attention_reason"> }) {
  if (account.status === "needs_attention") {
    return (
      <span
        className="pill pill-warn"
        title={`${account.attention_reason ?? "The provider refused it."} Fix it at the provider, then use “Fixed — retry these” on the Social Calendar.`}
      >
        needs attention
      </span>
    );
  }
  return <span className={account.status === "active" ? "pill pill-ok" : "pill pill-muted"}>{account.status}</span>;
}

interface SocialAccountsResponse {
  data: SocialAccount[];
}

/** CCM-87 — GET /voice-profiles?agencyclient_id= response row (subset). */
interface VoiceProfileOption {
  id: number;
  name: string;
}

export function SocialAccountsView() {
  const { data: workspaces, isLoading, error } =
    useSettingsWorkspaces<Agencyclient>();

  const [selectedWsId, setSelectedWsId] = useState<number | null>(() => {
    if (typeof window === "undefined") return null;
    const raw = window.sessionStorage.getItem(SA_WS_KEY);
    return raw ? Number(raw) : null;
  });

  useEffect(() => {
    if (selectedWsId != null) {
      try {
        window.sessionStorage.setItem(SA_WS_KEY, String(selectedWsId));
      } catch {
        /* tolerate */
      }
    }
  }, [selectedWsId]);

  useEffect(() => {
    if (workspaces && workspaces.length > 0) {
      const stillValid = workspaces.some((w) => w.id === selectedWsId);
      if (!stillValid) setSelectedWsId(workspaces[0].id);
    }
  }, [workspaces, selectedWsId]);

  if (isLoading) {
    return (
      <main className="main settings-main">
        <div className="settings-page">
          <Header />
          <div className="state-loading">Loading social accounts…</div>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="main settings-main">
        <div className="settings-page">
          <Header />
          <div className="state-error">
            {error instanceof ApiError && error.status === 403
              ? "You don't have permission to view social accounts."
              : (error.message ?? "Could not load workspaces.")}
          </div>
        </div>
      </main>
    );
  }

  if (!workspaces || workspaces.length === 0) {
    return (
      <main className="main settings-main">
        <div className="settings-page">
          <Header />
          <div className="settings-empty">
            <h2>No workspaces yet</h2>
            <p>Create a workspace under Settings → Workspaces before connecting social accounts.</p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <SocialAccountsBody
      workspaces={workspaces}
      selectedWsId={selectedWsId}
      onSelectWs={setSelectedWsId}
    />
  );
}

function Header() {
  return (
    <>
      <nav className="editor-breadcrumb" aria-label="Breadcrumb">
        <span>Settings</span>
        <span className="editor-breadcrumb-sep">/</span>
        <span>Social Accounts</span>
      </nav>
      <SectionHeader
        eyebrow="Settings · Social Accounts"
        title="Social Accounts"
        description="Connect social media accounts and share them with other workspaces you belong to."
      />
    </>
  );
}

interface BodyProps {
  workspaces: Agencyclient[];
  selectedWsId: number | null;
  onSelectWs: (id: number) => void;
}

function SocialAccountsBody({ workspaces, selectedWsId, onSelectWs }: BodyProps) {
  const activeWs = useMemo(
    () => workspaces.find((w) => w.id === selectedWsId) ?? workspaces[0],
    [workspaces, selectedWsId]
  );
  const activeWsId = activeWs.id;

  // S-199 pattern — sync the JWT agencyclient_id claim to the displayed
  // workspace so reads (RLS scope) and mutations (the connect/grant routes'
  // own claims.agencyclient_id checks) target it.
  useEnsureActiveWorkspace(activeWsId);

  const { activate, isActivating, error: switchError, clearError } =
    useActiveWorkspace();
  const [pendingWsId, setPendingWsId] = useState<number | null>(null);

  async function handleWorkspaceChange(nextId: number) {
    if (nextId === activeWsId) return;
    clearError();
    setPendingWsId(nextId);
    try {
      await activate(nextId);
      onSelectWs(nextId);
    } catch {
      /* useActiveWorkspace surfaces error via switchError */
    } finally {
      setPendingWsId(null);
    }
  }

  const qc = useQueryClient();
  const accountsQuery = useApiQuery<SocialAccountsResponse>(
    ["social-accounts", activeWsId],
    `/agencyclients/${activeWsId}/social-accounts`
  );
  const accounts = accountsQuery.data?.data ?? [];
  const ownedAccounts = accounts.filter((a) => !a.is_shared_to_me);
  const sharedAccounts = accounts.filter((a) => a.is_shared_to_me);

  // CCM-87 — fetched once here, shared by every row's voice-override select.
  const voiceProfilesQuery = useApiQuery<{ data: VoiceProfileOption[] }>(
    ["voice-profiles", activeWsId],
    `/voice-profiles?agencyclient_id=${activeWsId}`
  );
  const voiceProfileOptions = voiceProfilesQuery.data?.data ?? [];

  function invalidateAccounts() {
    void qc.invalidateQueries({ queryKey: ["social-accounts", activeWsId] });
  }

  const [grantingAccount, setGrantingAccount] = useState<SocialAccount | null>(null);
  const [disconnectingAccount, setDisconnectingAccount] = useState<SocialAccount | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const [banner, setBanner] = useState<{ kind: "success" | "error"; text: string } | null>(null);

  useEffect(() => {
    const connected = searchParams.get("zernio_connected");
    const errorCode = searchParams.get("zernio_connect_error");
    if (connected) {
      const label = PLATFORM_LABEL[connected as Platform] ?? connected;
      setBanner({ kind: "success", text: `Connected ${label} successfully.` });
      setSearchParams({}, { replace: true });
    } else if (errorCode) {
      setBanner({
        kind: "error",
        text: CONNECT_ERROR_MESSAGES[errorCode] ?? `Connection failed: ${errorCode}`,
      });
      setSearchParams({}, { replace: true });
    }
    // Runs once on mount to consume the callback's redirect params — not on
    // every searchParams change, which would re-fire after setSearchParams
    // clears them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main className="main settings-main">
      <div className="settings-page">
        <Header />

        <div className="team-toolbar">
          <span className="team-toolbar-label">Workspace</span>
          <select
            className="settings-select"
            value={pendingWsId ?? activeWsId}
            disabled={isActivating}
            onChange={(e) => {
              void handleWorkspaceChange(Number(e.target.value));
            }}
          >
            {workspaces.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name} ({w.slug})
              </option>
            ))}
          </select>
          {isActivating && <span className="team-toolbar-hint">Switching workspace…</span>}
          {switchError && !isActivating && (
            <span className="settings-field-error">
              {switchError.status === 403
                ? "You don't have access to that workspace."
                : (switchError.message ?? "Could not switch workspace.")}
            </span>
          )}
        </div>

        {banner && (
          <div
            className={banner.kind === "success" ? "team-banner" : "settings-form-error"}
            role="status"
            style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}
          >
            <span>{banner.text}</span>
            <button type="button" className="btn-ghost" onClick={() => setBanner(null)}>
              Dismiss
            </button>
          </div>
        )}

        <section>
          <div className="amp-rules-header" style={{ marginTop: 24 }}>
            <div>
              <h2>Your accounts</h2>
              <span className="amp-rules-header-sub">
                Accounts this workspace owns and can share with others.
              </span>
            </div>
          </div>

          {accountsQuery.isLoading && <div className="state-loading">Loading accounts…</div>}
          {accountsQuery.error && !accountsQuery.isLoading && (
            <div className="state-error">
              {accountsQuery.error.message ?? "Could not load accounts."}
            </div>
          )}

          {!accountsQuery.isLoading && !accountsQuery.error && (
            <>
              {ownedAccounts.length === 0 ? (
                <div className="panel-empty">No accounts connected yet.</div>
              ) : (
                <table className="workspaces-table">
                  <thead>
                    <tr>
                      <th></th>
                      <th>Account</th>
                      <th>Platform</th>
                      <th>Status</th>
                      <th>Voice</th>
                      <th>Shared with</th>
                      <th className="ws-actions-col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ownedAccounts.map((a) => (
                      <tr key={a.id} className="workspaces-row">
                        <td>
                          <PlatformIcon platform={a.platform as Platform} />
                        </td>
                        <td>{a.display_name}</td>
                        <td>
                          <span className="pill pill-info">{PLATFORM_LABEL[a.platform as Platform] ?? a.platform}</span>
                        </td>
                        <td>
                          <AccountStatusPill account={a} />
                        </td>
                        <td>
                          <VoiceOverrideSelect
                            account={a}
                            options={voiceProfileOptions}
                            onSaved={invalidateAccounts}
                          />
                        </td>
                        <td>
                          {a.shared_with && a.shared_with.length > 0 ? (
                            <div className="settings-form-actions">
                              {a.shared_with.map((s) => (
                                <RevokeGrantPill
                                  key={s.agencyclient_id}
                                  accountId={a.id}
                                  grant={s}
                                  onRevoked={invalidateAccounts}
                                />
                              ))}
                            </div>
                          ) : (
                            <span className="amp-rules-header-sub">Not shared</span>
                          )}
                        </td>
                        <td>
                          <div className="settings-form-actions">
                            <button type="button" className="btn-ghost" onClick={() => setGrantingAccount(a)}>
                              Share…
                            </button>
                            <button type="button" className="btn-ghost btn-danger" onClick={() => setDisconnectingAccount(a)}>
                              Disconnect
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <div className="amp-rules-header" style={{ marginTop: 16 }}>
                <span className="amp-rules-header-sub">Connect a new account:</span>
              </div>
              <div className="settings-form-actions">
                {PLATFORMS.map((platform) => (
                  <ConnectAccountControl key={platform} agencyclientId={activeWsId} platform={platform} />
                ))}
              </div>
            </>
          )}
        </section>

        <BufferChannelsSection agencyclientId={activeWsId} />

        {sharedAccounts.length > 0 && (
          <section>
            <div className="amp-rules-header" style={{ marginTop: 24 }}>
              <div>
                <h2>Shared with you</h2>
                <span className="amp-rules-header-sub">
                  Accounts owned by another workspace that this one may publish through.
                </span>
              </div>
            </div>
            <table className="workspaces-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Account</th>
                  <th>Platform</th>
                  <th>Status</th>
                  <th>Voice</th>
                </tr>
              </thead>
              <tbody>
                {sharedAccounts.map((a) => (
                  <tr key={a.id} className="workspaces-row">
                    <td>
                      <PlatformIcon platform={a.platform as Platform} />
                    </td>
                    <td>{a.display_name}</td>
                    <td>
                      <span className="pill pill-info">{PLATFORM_LABEL[a.platform as Platform] ?? a.platform}</span>
                    </td>
                    <td>
                      {a.status === "needs_attention" ? (
                        <AccountStatusPill account={a} />
                      ) : (
                        <span className="pill pill-muted">shared</span>
                      )}
                    </td>
                    <td>
                      {/* Read-only — only the owning workspace may edit its own
                          account's override (PATCH /social-accounts/:id is
                          403 for a grantee), and the override's own voice
                          profile lives in the owner's workspace, not readable
                          cross-workspace here. */}
                      {a.voice_profile_id != null ? (
                        <span className="pill pill-info">Has override</span>
                      ) : (
                        <span className="amp-rules-header-sub">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {grantingAccount && (
          <GrantAccountModal
            account={grantingAccount}
            onClose={() => setGrantingAccount(null)}
            onGranted={() => {
              invalidateAccounts();
              setGrantingAccount(null);
            }}
          />
        )}

        {disconnectingAccount && (
          <ConfirmDisconnectModal
            account={disconnectingAccount}
            onCancel={() => setDisconnectingAccount(null)}
            onDisconnected={() => {
              invalidateAccounts();
              setDisconnectingAccount(null);
            }}
          />
        )}
      </div>
    </main>
  );
}

// ---------- Buffer channels (FEAT-097) ----------

const CHANNEL_STATE_PILL: Record<ChannelState, { className: string; label: string }> = {
  available: { className: "pill pill-info", label: "Available" },
  imported: { className: "pill pill-ok", label: "Imported" },
  unsupported: { className: "pill pill-muted", label: "Not supported yet" },
};

// Channels live in the user's own Buffer account — connected there, not here.
// Keyed under "social-accounts" so useActiveWorkspace's prefix invalidation
// refetches it after a workspace switch; the route 403s until the JWT's
// workspace matches the path.
function BufferChannelsSection({ agencyclientId }: { agencyclientId: number }) {
  const qc = useQueryClient();
  const query = useApiQuery<{ data: BufferChannelRow[] }>(
    ["social-accounts", "buffer-channels", agencyclientId],
    `/agencyclients/${agencyclientId}/social-accounts/buffer/channels`,
    // A 409 (no Buffer key) is the normal state for most workspaces — retrying it is noise.
    { retry: false }
  );
  const error = query.error ? describeChannelsError(query.error) : null;
  const channels = query.data?.data ?? [];

  const importMutation = useApiMutation<unknown, { buffer_channel_id: string }>(
    `/agencyclients/${agencyclientId}/social-accounts/buffer/import`,
    "POST",
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: ["social-accounts", "buffer-channels", agencyclientId] });
        void qc.invalidateQueries({ queryKey: ["social-accounts", agencyclientId] });
      },
    }
  );
  const [importingId, setImportingId] = useState<string | null>(null);

  function handleImport(channelId: string) {
    setImportingId(channelId);
    importMutation.mutate(
      { buffer_channel_id: channelId },
      { onSettled: () => setImportingId(null) }
    );
  }

  return (
    <section>
      <div className="amp-rules-header" style={{ marginTop: 24 }}>
        <div>
          <h2>Buffer channels</h2>
          <span className="amp-rules-header-sub">
            Channels connected inside your own Buffer account. Substack Notes publish through Buffer.
          </span>
        </div>
      </div>

      {query.isLoading && <div className="state-loading">Loading Buffer channels…</div>}
      {error?.kind === "no_key" && (
        <div className="panel-empty">
          No Buffer API key for this workspace. Add one under Settings → API Keys to use the channels you've connected in
          Buffer.
        </div>
      )}
      {error?.kind === "error" && <div className="state-error">{error.text}</div>}

      {query.data &&
        (channels.length === 0 ? (
          <div className="panel-empty">Your Buffer account has no channels connected yet.</div>
        ) : (
          <table className="workspaces-table">
            <thead>
              <tr>
                <th>Channel</th>
                <th>Buffer service</th>
                <th>Status</th>
                <th className="ws-actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {channels.map((ch) => {
                const state = channelState(ch);
                const pill = CHANNEL_STATE_PILL[state];
                return (
                  <tr key={ch.buffer_channel_id} className="workspaces-row">
                    <td>{ch.name}</td>
                    <td>{ch.service}</td>
                    <td>
                      <span className={pill.className}>{pill.label}</span>
                    </td>
                    <td>
                      {state === "available" && (
                        <button
                          type="button"
                          className="btn-ghost"
                          disabled={importingId === ch.buffer_channel_id}
                          onClick={() => handleImport(ch.buffer_channel_id)}
                        >
                          {importingId === ch.buffer_channel_id ? "Importing…" : "Import"}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ))}
      {importMutation.isError && (
        <div className="settings-form-error">{describeApiErrorMessage(importMutation.error)}</div>
      )}
    </section>
  );
}

// ---------- Connect control ----------

// BUG-091 — the connect button is always available per platform, never
// hidden once one account of that platform exists. social_accounts has no
// one-account-per-platform constraint (only social_accounts_provider_
// external_unique, which stops the SAME external account being connected
// twice, not a second DIFFERENT one) — a workspace can hold as many
// accounts of a platform as it actually has. The old ZernioConnectControl
// this was modeled on hid the button once connected because ITS data model
// forced one account per platform (a single dispatch-account-id column per
// amplifier row) — the new table has no such shape, and the UI shouldn't
// have carried that behavior over without the reason for it.
//
// Founder request (2026-09-18) — the button label is just the platform
// name now ("LinkedIn", not "Connect another LinkedIn account"): the
// section header above this row of buttons already says "Connect a new
// account:", so spelling out "Connect" and "account" on every button was
// redundant regardless of whether one was already connected — no need for
// a hasExisting distinction in the label at all. Also switched from
// btn-ghost (only shows a border/background on hover) to btn-primary so
// these read as buttons at rest, not as plain text.
function ConnectAccountControl({
  agencyclientId,
  platform,
}: {
  agencyclientId: number;
  platform: Platform;
}) {
  const api = useApi();
  const connect = useMutation({
    mutationFn: async () => {
      const { auth_url } = await api<{ auth_url: string }>(
        `/agencyclients/${agencyclientId}/social-accounts/connect`,
        { method: "POST", body: JSON.stringify({ platform }) }
      );
      return auth_url;
    },
    onSuccess: (authUrl) => {
      window.location.href = authUrl;
    },
  });

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className="btn-primary"
        disabled={connect.isPending}
        onClick={() => connect.mutate()}
      >
        {connect.isPending ? "…" : PLATFORM_LABEL[platform]}
      </button>
      {connect.isError && (
        <span className="settings-field-error">
          {connect.error instanceof ApiError
            ? connect.error.message
            : `Could not start the connect flow for ${PLATFORM_LABEL[platform]}.`}
        </span>
      )}
    </div>
  );
}

// ---------- Voice override (CCM-87) ----------

function VoiceOverrideSelect({
  account,
  options,
  onSaved,
}: {
  account: SocialAccount;
  options: VoiceProfileOption[];
  onSaved: () => void;
}) {
  const api = useApi();
  const update = useMutation({
    mutationFn: (voiceProfileId: number | null) =>
      api(`/social-accounts/${account.id}`, {
        method: "PATCH",
        body: JSON.stringify({ voice_profile_id: voiceProfileId }),
      }),
    onSuccess: onSaved,
  });

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <select
        className="settings-select"
        value={account.voice_profile_id ?? ""}
        disabled={update.isPending}
        onChange={(e) => update.mutate(e.target.value ? Number(e.target.value) : null)}
      >
        <option value="">Publication default</option>
        {options.map((v) => (
          <option key={v.id} value={v.id}>
            {v.name}
          </option>
        ))}
      </select>
      {update.isError && (
        <span className="settings-field-error">
          {update.error instanceof ApiError ? update.error.message : "Could not update the voice override."}
        </span>
      )}
    </div>
  );
}

// ---------- Grant / revoke ----------

const GRANT_ERROR_MESSAGES: Record<string, string> = {
  not_owner: "Only the owning workspace may share this account.",
  not_a_member: "You must belong to the target workspace to share into it.",
  already_granted: "This account is already shared with that workspace.",
};

function grantErrorMessage(err: ApiError): string {
  try {
    const parsed = JSON.parse(err.body) as { error?: string };
    if (parsed.error && GRANT_ERROR_MESSAGES[parsed.error]) return GRANT_ERROR_MESSAGES[parsed.error];
  } catch {
    /* fall through to the generic message below */
  }
  return err.message;
}

function GrantAccountModal({
  account,
  onClose,
  onGranted,
}: {
  account: SocialAccount;
  onClose: () => void;
  onGranted: () => void;
}) {
  // GET /agencyclients/me, not the page's own /agencyclients selector — the
  // grant route requires the granting user to actually belong to the target
  // workspace (social-accounts.ts:399-410's not_a_member check), so only
  // offering workspaces the user can act within avoids a picker option that
  // would just 403.
  const { data: myWorkspaces, isLoading } = useApiQuery<Agencyclient[]>(
    ["agencyclients", "me"],
    "/agencyclients/me"
  );
  const candidates = (myWorkspaces ?? []).filter((w) => w.id !== account.owning_agencyclient_id);
  const [granteeId, setGranteeId] = useState<number | null>(null);

  const grant = useApiMutation<unknown, { grantee_agencyclient_id: number }>(
    `/social-accounts/${account.id}/grants`,
    "POST",
    { onSuccess: onGranted }
  );

  return (
    <ModalShell onClose={onClose} eyebrow="Social Accounts · share" title={`Share ${account.display_name}`}>
      {isLoading && <div className="state-loading">Loading workspaces…</div>}
      {!isLoading && candidates.length === 0 && (
        <p className="settings-confirm-copy">
          You don't belong to any other workspace to share this account with.
        </p>
      )}
      {!isLoading && candidates.length > 0 && (
        <>
          <label className="team-toolbar-label" htmlFor="grantee-select" style={{ display: "block", marginBottom: 6 }}>
            Share with workspace
          </label>
          <select
            id="grantee-select"
            className="settings-select"
            value={granteeId ?? ""}
            onChange={(e) => setGranteeId(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">Select a workspace…</option>
            {candidates.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name} ({w.slug})
              </option>
            ))}
          </select>
          {grant.error && (
            <div className="settings-form-error">{grantErrorMessage(grant.error)}</div>
          )}
          <div className="settings-form-actions">
            <button type="button" className="btn-ghost" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={granteeId == null || grant.isPending}
              onClick={() => granteeId != null && grant.mutate({ grantee_agencyclient_id: granteeId })}
            >
              {grant.isPending ? "Sharing…" : "Share"}
            </button>
          </div>
        </>
      )}
    </ModalShell>
  );
}

function RevokeGrantPill({
  accountId,
  grant,
  onRevoked,
}: {
  accountId: number;
  grant: SharedWithEntry;
  onRevoked: () => void;
}) {
  const revoke = useApiMutation<void, void>(
    `/social-accounts/${accountId}/grants/${grant.agencyclient_id}`,
    "DELETE",
    { onSuccess: onRevoked }
  );

  return (
    <span className="pill pill-info">
      {grant.agencyclient_name}
      <button
        type="button"
        className="btn-ghost"
        disabled={revoke.isPending}
        onClick={() => revoke.mutate()}
        style={{ marginLeft: 6 }}
        aria-label={`Revoke sharing with ${grant.agencyclient_name}`}
      >
        ×
      </button>
    </span>
  );
}

// ---------- Disconnect ----------

// FEAT-095 correction (2026-09-17) / BUG-092 (CCM-83) — disconnecting really
// disconnects the account from Zernio (and may delete its Zernio profile,
// invisibly), so this gets the same explicit confirm treatment
// ApiKeysView.tsx's ConfirmDeleteModal uses for deleting a credential, not a
// bare inline button like RevokeGrantPill's trivially-reversible revoke.
function ConfirmDisconnectModal({
  account,
  onCancel,
  onDisconnected,
}: {
  account: SocialAccount;
  onCancel: () => void;
  onDisconnected: () => void;
}) {
  const mutation = useApiMutation<void, void>(`/social-accounts/${account.id}`, "DELETE", {
    onSuccess: onDisconnected,
  });

  return (
    <ModalShell onClose={onCancel} eyebrow="Social Accounts · danger" title={`Disconnect ${account.display_name}?`}>
      <p className="settings-confirm-copy">
        This really disconnects <strong>{account.display_name}</strong> from Zernio — not just from
        this list. It cannot be undone from here; you would need to reconnect it from scratch.
      </p>
      {mutation.error && <div className="settings-form-error">{mutation.error.message}</div>}
      <div className="settings-form-actions">
        <button type="button" className="btn-ghost" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="btn-primary btn-danger"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
        >
          {mutation.isPending ? "Disconnecting…" : "Disconnect"}
        </button>
      </div>
    </ModalShell>
  );
}
